/**
 * Console (reference 04, A-22): the engine Log, as Unreal's Output Log. Rows
 * show time, category and message, styled by verbosity. A category filter
 * narrows the list; `Cmd` shows every command the editor ran, with its origin.
 * Typed commands would be a front-end to the same bus; until they exist there's
 * no command row, rather than a disabled one promising them.
 */
import { useState } from "react";
import type { LogEntry } from "../../core/Log";
import { formatLogTime } from "../format/numbers";
import { useLog } from "../hooks/editorHooks";
import { FilterSelect } from "../ui/FilterSelect";
import { useTourTarget } from "../tour/TourTargets";
import { filterLog, logCategories } from "./logFilter";
import { VirtualList } from "./VirtualList";
import "./log.css";

export function ConsolePanel() {
  const { entries } = useLog();
  const [category, setCategory] = useState<string | null>(null);
  const tourTarget = useTourTarget("console");

  return (
    <div ref={tourTarget} className="kn-log" data-testid="console">
      <CategoryFilter categories={logCategories(entries)} value={category} onChange={setCategory} />
      <VirtualList
        items={filterLog(entries, category)}
        label="Console"
        rowKey={(entry) => entry.seq}
        renderRow={(entry) => <LogRow entry={entry} />}
      />
    </div>
  );
}

interface CategoryFilterProps {
  readonly categories: readonly string[];
  readonly value: string | null;
  readonly onChange: (category: string | null) => void;
}

/** "All categories" or one of them. A chosen category stays listed even before new rows use it. */
function CategoryFilter({ categories, value, onChange }: CategoryFilterProps) {
  const options =
    value !== null && !categories.includes(value) ? [...categories, value] : categories;
  return (
    <div className="kn-log__toolbar">
      <FilterSelect
        label="Category"
        value={value ?? ""}
        options={[
          { value: "", label: "All categories" },
          ...options.map((category) => ({ value: category, label: category })),
        ]}
        onChange={(chosen) => {
          onChange(chosen === "" ? null : chosen);
        }}
      />
    </div>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  return (
    <span className="kn-log__row" data-verbosity={entry.verbosity}>
      <span className="kn-log__time">{formatLogTime(entry.time)}</span>
      <span className="kn-log__category">{entry.category}</span>
      <span className="kn-log__message">{entry.message}</span>
    </span>
  );
}
